/**
 * Event batching for the trusted origin.
 *
 * The POC proved that rich instrumentation is what makes adaptivity possible — not just page
 * views and completions, but *which* wrong answer, *which* direction she dragged, and which
 * gestures went nowhere. Two things follow:
 *
 *  1. Instrumentation is infrastructure, not lesson-author work. `track()` is called by the
 *     renderer and the sims, so a lesson cannot ship uninstrumented.
 *  2. Events are batched. A drag emits a lot of small facts; one POST per fact would be a
 *     denial-of-service on our own Worker. Flush on a 600ms debounce, on tab hide, and via
 *     sendBeacon on pagehide so the last batch of a session is never lost.
 */

import type {
  EventBatchRequest,
  EventBatchResponse,
  EventInput,
  EventKind,
} from "../shared/api.js";
import { cap } from "./dom.js";
import { stampVersion } from "./answer-version.js";

const SESSION_KEY = "tutor.sid";
const FLUSH_DEBOUNCE_MS = 600;
/** A single batch bigger than this is a bug somewhere; flush early rather than grow forever. */
const MAX_QUEUE = 40;

export interface TelemetryConfig {
  studentSlug: string;
  /** Present on a lesson page, absent on the home page. */
  lessonSlug?: string;
}

interface State {
  studentSlug: string;
  lessonSlug?: string;
  endpoint: string;
}

let state: State | null = null;
let queue: EventInput[] = [];
let flushTimer: number | undefined;
/** Set by the server's reply so the chat panel can show a pending bubble without guessing. */
const listeners: Array<(thinking: boolean, queued: boolean) => void> = [];

/** A stable id per browser tab. sessionStorage, so a new tab is a new session. */
export const sessionId: string = readSessionId();

function readSessionId(): string {
  try {
    const existing = sessionStorage.getItem(SESSION_KEY);
    if (existing) return existing;
    const fresh = newId();
    sessionStorage.setItem(SESSION_KEY, fresh);
    return fresh;
  } catch {
    // Private mode with storage disabled: fall back to a per-load id rather than failing.
    return newId();
  }
}

function newId(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === "function") return c.randomUUID();
  return `${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;
}

export function initTelemetry(config: TelemetryConfig): void {
  state = {
    studentSlug: config.studentSlug,
    ...(config.lessonSlug === undefined ? {} : { lessonSlug: config.lessonSlug }),
    endpoint: `/api/students/${encodeURIComponent(config.studentSlug)}/events`,
  };

  // Hiding the tab is the most reliable "the child stopped" signal we get on mobile.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush(true);
  });
  // pagehide fires where unload does not (Safari's back/forward cache). beforeunload is
  // deliberately not used: registering it disables that cache.
  addEventListener("pagehide", () => flush(true));
}

/**
 * Notified with `thinking` from each batch response.
 *
 * This is how a *behavioural* trigger gets the same immediate feedback a typed question does. If a
 * child drags something and the ladder decides to respond, the server sets `thinking` on the batch
 * reply and she sees a pending bubble — rather than nothing at all until the answer lands, which is
 * the failure mode "silence after action makes the system feel broken" describes.
 */
export function onThinking(listener: (thinking: boolean, queued: boolean) => void): void {
  listeners.push(listener);
}

/**
 * Event kinds the server may answer, mirroring `shouldConsider` in `src/routes/api.ts`.
 *
 * Duplicated deliberately rather than shared: the client is *predicting* what the server will do so
 * it can show a pending bubble before the answer exists. A drift here costs a bubble that should not
 * have appeared (or one that should have), never a wrong reply — and the server stays the only thing
 * that actually decides.
 */
const PROVOKES_A_REPLY = new Set(["chat", "answer", "lesson_complete", "interact_miss"]);

export function track(kind: EventKind, payload: Record<string, unknown> = {}): void {
  if (!state) return;
  const event: EventInput = {
    kind,
    sessionId,
    page: location.pathname,
    ...(state.lessonSlug === undefined ? {} : { lessonSlug: state.lessonSlug }),
    // An answer names the version of the page it was given on, stamped NOW rather than at flush.
    payload: stampVersion(kind, payload),
  };
  queue.push(event);
  if (queue.length >= MAX_QUEUE) {
    flush(false);
    return;
  }
  if (flushTimer !== undefined) clearTimeout(flushTimer);
  flushTimer = setTimeout(() => {
    flushTimer = undefined;
    flush(false);
  }, FLUSH_DEBOUNCE_MS) as unknown as number;
}

/**
 * @param beacon send synchronously via navigator.sendBeacon — the only thing that survives the
 * page going away.
 */
export function flush(beacon: boolean): void {
  if (!state || queue.length === 0) return;
  if (flushTimer !== undefined) {
    clearTimeout(flushTimer);
    flushTimer = undefined;
  }
  const body: EventBatchRequest = { events: queue };
  queue = [];
  const json = JSON.stringify(body);

  /**
   * Raise the pending bubble on the way OUT, not on the way back.
   *
   * The server used to answer this batch instantly with `thinking: true` and do the work afterwards
   * in `waitUntil` — which Cloudflare kills 30 seconds after the response, so the work never
   * finished in production. The turn now runs inside the request, which means the reply arrives at
   * the END of the ~40-70 seconds rather than the start, and a client that waits to be told would
   * show nothing at all for the whole wait. That silence after an action is the exact failure the
   * indicator exists to remove.
   *
   * So the client predicts it, using the same event kinds the server treats as significant. Being
   * wrong is cheap and self-correcting: Tier 0 may decide the batch was not worth answering, and the
   * bubble then clears when the response says `thinking: false`.
   */
  const raised = body.events.some((event) => PROVOKES_A_REPLY.has(event.kind));
  if (raised) {
    for (const listener of listeners) listener(true, false);
  }

  if (beacon && typeof navigator.sendBeacon === "function") {
    // The Content-Type has to be set through the Blob; sendBeacon takes no headers.
    const blob = new Blob([json], { type: "application/json" });
    if (navigator.sendBeacon(state.endpoint, blob)) return;
    // Fall through to fetch if the beacon was refused (queue full, size limit).
  }

  void fetch(state.endpoint, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: json,
    keepalive: true,
    credentials: "same-origin",
  })
    .then(async (response) => {
      if (!response.ok) {
        // A refused batch (rate limited, tutor unavailable) gets no reply, so take down the bubble
        // this flush put up — otherwise it spins until the indicator's own long timeout gives up.
        if (raised) for (const listener of listeners) listener(false, false);
        return;
      }
      const parsed = (await response.json()) as Partial<EventBatchResponse>;
      if (typeof parsed.thinking === "boolean") {
        for (const listener of listeners) listener(parsed.thinking, parsed.queued === true);
      }
    })
    .catch(() => {
      // Telemetry is best-effort by design. A dropped batch must never break the lesson.
    });
}

// ───────────────────────────── interact_miss detection ─────────────────────────────

/*
 * Ported from the reference POC's app.js, which is the only part of its telemetry that found
 * design bugs on its own: a child who tries to drag something undraggable, or taps something
 * that looks tappable and isn't, produces an event instead of silent frustration.
 *
 * Two detectors:
 *   drag-attempt     pointerdown then >18px of movement on a prominent, non-interactive element
 *   click-no-handler a click that lands on a prominent, non-interactive element
 *
 * The exclusions matter as much as the detection. Text elements are excluded because a drag
 * across a paragraph is selection, not a failed affordance; the chat is excluded because it is a
 * scrollable, selectable surface rather than a diagram.
 */

const DRAG_THRESHOLD_PX = 18;

/** Tags that are usually text being read or selected, not affordances. */
const TEXT_TAGS = new Set([
  "P", "SPAN", "H1", "H2", "H3", "H4", "H5", "H6", "LI", "EM", "STRONG",
  "UL", "OL", "BLOCKQUOTE", "FIGCAPTION", "LABEL", "TD", "TH", "CODE", "PRE",
]);

const INTERACTIVE_SELECTOR =
  "[data-track], [data-has-handler], button, a, input, select, textarea, label, summary, [role='button'], [contenteditable='true']";

/** Surfaces where a drag or stray click is expected behaviour, not a failed affordance. */
const EXEMPT_SELECTOR = ".chat, .chat-log, .msg, .chat-input, .sim-stage, .draw-surface, [data-no-miss]";

/**
 * Layout containers. A gesture whose target is one of these landed on page background — usually in
 * the padding between blocks — and says nothing about any particular affordance.
 */
const CONTAINER_SELECTOR = "html, body, .app, .app-main, .app-chat, .lesson, .lesson-head, .block, .btn-row, .sim, .sim-holder, .frame-wrap";

/**
 * The share of the viewport above which an element is background rather than a target.
 *
 * The POC used an absolute 200,000px² cap. That number was tuned for its fixed 1200px desktop grid
 * and does not survive this layout: an ordinary figure in a 655px column measures ~204,000px², so
 * the absolute cap silently excluded exactly the elements this detector exists to catch — big
 * diagrams a child tries to drag. Expressed against the viewport, "too big to be a target" means
 * the same thing on a phone and on a desktop.
 */
const MAX_TARGET_VIEWPORT_SHARE = 0.6;

function isElement(value: EventTarget | null): value is Element {
  return value !== null && value instanceof Element;
}

function looksInteractive(target: Element): boolean {
  if (target.closest(INTERACTIVE_SELECTOR)) return true;
  if (target.closest(EXEMPT_SELECTOR)) return true;
  const cursor = getComputedStyle(target).cursor;
  return cursor === "pointer" || cursor === "grab" || cursor === "grabbing";
}

/** Big enough to be a deliberate target, small enough not to be the page background. */
function looksProminent(target: Element): boolean {
  if (target.matches(CONTAINER_SELECTOR)) return false;
  const rect = target.getBoundingClientRect();
  if (rect.width < 24 || rect.height < 24) return false;
  const viewport = Math.max(1, innerWidth * innerHeight);
  return rect.width * rect.height < viewport * MAX_TARGET_VIEWPORT_SHARE;
}

function describeTarget(target: Element): string {
  const tag = target.tagName.toLowerCase();
  const id = target.id ? `#${target.id}` : "";
  const classes =
    typeof target.className === "string" && target.className.trim()
      ? `.${target.className.trim().split(/\s+/).slice(0, 2).join(".")}`
      : "";
  const text = cap((target.textContent ?? "").trim(), 40);
  return `${tag}${id}${classes}${text ? `:${JSON.stringify(text)}` : ""}`;
}

function nearestBlockId(target: Element): string {
  const holder = target.closest("[data-block-id]");
  return holder instanceof HTMLElement ? cap(holder.dataset["blockId"], 64) : "";
}

interface DragCandidate {
  target: Element;
  x: number;
  y: number;
}

let dragSuspected: DragCandidate | null = null;

function candidateFor(target: EventTarget | null): Element | null {
  if (!isElement(target)) return null;
  if (TEXT_TAGS.has(target.tagName)) return null;
  if (looksInteractive(target)) return null;
  if (!looksProminent(target)) return null;
  return target;
}

/** Installs the global miss detectors. Safe to call once per page. */
export function installMissDetection(): void {
  document.addEventListener(
    "pointerdown",
    (event) => {
      const target = candidateFor(event.target);
      dragSuspected = target ? { target, x: event.clientX, y: event.clientY } : null;
    },
    true,
  );

  document.addEventListener(
    "pointermove",
    (event) => {
      const suspect = dragSuspected;
      if (!suspect) return;
      const dx = event.clientX - suspect.x;
      const dy = event.clientY - suspect.y;
      if (Math.hypot(dx, dy) <= DRAG_THRESHOLD_PX) return;
      dragSuspected = null;
      track("interact_miss", {
        action: "drag-attempt",
        target: describeTarget(suspect.target),
        blockId: nearestBlockId(suspect.target),
        dx: Math.round(dx),
        dy: Math.round(dy),
      });
    },
    true,
  );

  const cancel = (): void => {
    dragSuspected = null;
  };
  document.addEventListener("pointerup", cancel, true);
  document.addEventListener("pointercancel", cancel, true);

  document.addEventListener(
    "click",
    (event) => {
      const target = candidateFor(event.target);
      if (!target) return;
      track("interact_miss", {
        action: "click-no-handler",
        target: describeTarget(target),
        blockId: nearestBlockId(target),
      });
    },
    true,
  );
}
